import { hashKey, useQuery } from '@tanstack/react-query';

import { Batch, BatchTypeEnum, IBatchesCriteria, SortDirection, TemplateType } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { showErrorToast } from '@hooks/showErrorToast';
import { request, STORAGE_KEYS } from '@utils';
import { getStoredCriteria } from '../../../criteria.caching';

const defaultBatchesCriteria: IBatchesCriteria = {
  filters: { filterByUsers: [], filterByNotifications: [] },
  pagination: { page: 1, size: 10, direction: 1, searchToken: '', currentToken: '' },
  sorting: { field: 'counter', direction: SortDirection.DESC },
};

export const getInitialCriteria = () => {
  const storedCriteria = getStoredCriteria(STORAGE_KEYS.COOKIES.LIST_BATCHES_CRITERIA);
  const criteria = storedCriteria ?? defaultBatchesCriteria;

  const batchView = getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum;
  if (!batchView || [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
    criteria.filters.templateType = TemplateType.HIGH_VALUE_H1;
  } else {
    criteria.filters.templateType = TemplateType.LOW_VALUE_H7;
  }

  return criteria;
};

const fetchBatches = async (criteria: IBatchesCriteria): Promise<{ list: Batch[]; total: number }> => {
  const { filters, sorting, pagination } = criteria;
  try {
    const {
      data: { list, totalItems },
    } = await request({
      path: `batches`,
      method: 'POST',
      authenticate: true,
      dataObject: {
        criteria: {
          filters,
          sorting,
          pagination,
        },
      },
    });
    return { list: list.map((b) => ({ ...b, createdAt: new Date(b.createdAt), updatedAt: new Date(b.updatedAt) })), total: totalItems };
  } catch (error) {
    showErrorToast(error, 'Failed to fetch batches');
  }
};

export const useBatches = (criteria: IBatchesCriteria) =>
  useQuery({
    queryKey: ['batches', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });

export const useBatchesCustomsReport = (criteria: IBatchesCriteria) =>
  useQuery({
    queryKey: ['batches-customs-report', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });

export const prefetchBatches = (criteria: IBatchesCriteria) =>
  queryClient.prefetchQuery({
    queryKey: ['batches', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });
